import { format, startOfWeek, endOfWeek, eachDayOfInterval, addDays, subDays, differenceInDays, subYears } from 'date-fns';
import type { Timesheet, Employee, Project } from '@/types/timesheet';
import type { UtilisationMetrics, DailyUtilisation, ProjectUtilisation, UtilisationTrend, UtilisationSummary, UtilisationReportData, UtilisationFilters, ComparisonMode, ComparisonMetrics, ComparisonSummary, CapacityMetrics, CapacityRecommendation, CapacitySummary } from '@/types/utilisation';
import type { UtilisationSettings } from '@/types/timesheetSettings';

const TARGET_UTILISATION_RATE = 0.75; // 75% target utilisation

/**
 * Get employee's expected hours per week from their working pattern
 */
const getEmployeeExpectedHoursPerWeek = (employee: Employee): number => {
  if (employee.workingPattern?.standardHoursPerWeek) {
    return employee.workingPattern.standardHoursPerWeek;
  }
  // Fallback to 40h if no working pattern is set
  return 40;
};

/**
 * Calculate utilisation metrics for a single employee
 */
export const calculateEmployeeUtilisation = (
  employee: Employee,
  timesheets: Timesheet[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): UtilisationMetrics => {
  const employeeTimesheets = timesheets.filter(
    (ts) =>
      ts.employeeId === employee.id &&
      ts.weekEnding >= dateRange.start &&
      ts.weekEnding <= dateRange.end &&
      (ts.status === 'approved' || ts.status === 'locked')
  );

  let totalHours = 0;
  let billableHours = 0;
  const dailyHours: { [key: string]: number } = {};
  const projectIds = new Set<string>();

  employeeTimesheets.forEach((ts) => {
    ts.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      projectIds.add(entry.projectId);

      Object.entries(entry.hours).forEach(([day, hours]) => {
        totalHours += hours;
        if (project?.billable) {
          billableHours += hours;
        }

        if (!dailyHours[day]) {
          dailyHours[day] = 0;
        }
        dailyHours[day] += hours;
      });
    });
  });

  const nonBillableHours = totalHours - billableHours;
  const utilisationRate = totalHours > 0 ? billableHours / totalHours : 0;

  // Find peak day
  let peakDay = 'monday';
  let peakHours = 0;
  Object.entries(dailyHours).forEach(([day, hours]) => {
    if (hours > peakHours) {
      peakDay = day;
      peakHours = hours;
    }
  });

  // Calculate target hours based on weeks in range and employee's working pattern
  const weeksInRange = Math.ceil(
    (dateRange.end.getTime() - dateRange.start.getTime()) / (7 * 24 * 60 * 60 * 1000)
  );
  const expectedHoursPerWeek = getEmployeeExpectedHoursPerWeek(employee);
  const targetHours = weeksInRange * expectedHoursPerWeek;
  const variance = totalHours - targetHours;

  return {
    employeeId: employee.id,
    employeeName: employee.name,
    department: employee.department,
    totalHours,
    billableHours,
    nonBillableHours,
    utilisationRate,
    targetHours,
    variance,
    peakDay,
    peakHours,
    projectCount: projectIds.size,
  };
};

/**
 * Calculate daily utilisation across all employees
 */
export const calculateDailyUtilisation = (
  timesheets: Timesheet[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): DailyUtilisation[] => {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dailyData: { [key: string]: { total: number; billable: number } } = {};

  days.forEach((day) => {
    dailyData[day] = { total: 0, billable: 0 };
  });

  const relevantTimesheets = timesheets.filter(
    (ts) =>
      ts.weekEnding >= dateRange.start &&
      ts.weekEnding <= dateRange.end &&
      (ts.status === 'approved' || ts.status === 'locked')
  );

  relevantTimesheets.forEach((ts) => {
    ts.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);

      Object.entries(entry.hours).forEach(([day, hours]) => {
        dailyData[day].total += hours;
        if (project?.billable) {
          dailyData[day].billable += hours;
        }
      });
    });
  });

  return days.map((day, index) => {
    const data = dailyData[day];
    const utilisationRate = data.total > 0 ? data.billable / data.total : 0;

    // Create a date for the day (using current week as reference)
    const today = new Date();
    const weekStart = startOfWeek(today, { weekStartsOn: 1 });
    const date = addDays(weekStart, index);

    return {
      date,
      day,
      totalHours: data.total,
      billableHours: data.billable,
      nonBillableHours: data.total - data.billable,
      utilisationRate,
    };
  });
};

/**
 * Calculate project utilisation metrics
 */
export const calculateProjectUtilisation = (
  timesheets: Timesheet[],
  projects: Project[],
  employees: Employee[],
  dateRange: { start: Date; end: Date }
): ProjectUtilisation[] => {
  const projectData: {
    [key: string]: {
      totalHours: number;
      billableHours: number;
      employeeIds: Set<number>;
    };
  } = {};

  const relevantTimesheets = timesheets.filter(
    (ts) =>
      ts.weekEnding >= dateRange.start &&
      ts.weekEnding <= dateRange.end &&
      (ts.status === 'approved' || ts.status === 'locked')
  );

  relevantTimesheets.forEach((ts) => {
    ts.entries.forEach((entry) => {
      if (!projectData[entry.projectId]) {
        projectData[entry.projectId] = {
          totalHours: 0,
          billableHours: 0,
          employeeIds: new Set(),
        };
      }

      const project = projects.find((p) => p.id === entry.projectId);
      const totalEntryHours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);

      projectData[entry.projectId].totalHours += totalEntryHours;
      if (project?.billable) {
        projectData[entry.projectId].billableHours += totalEntryHours;
      }
      projectData[entry.projectId].employeeIds.add(ts.employeeId);
    });
  });

  return Object.entries(projectData).map(([projectId, data]) => {
    const project = projects.find((p) => p.id === projectId);
    const utilisationRate = data.totalHours > 0 ? data.billableHours / data.totalHours : 0;

    return {
      projectId,
      projectCode: project?.code || 'N/A',
      projectName: project?.name || 'Unknown Project',
      clientName: project?.clientName || 'Unknown Client',
      totalHours: data.totalHours,
      billableHours: data.billableHours,
      employeeCount: data.employeeIds.size,
      utilisationRate,
      isBillable: project?.billable || false,
    };
  });
};

/**
 * Calculate utilisation trends over time
 */
export const calculateUtilisationTrends = (
  timesheets: Timesheet[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): UtilisationTrend[] => {
  const weeklyData: {
    [key: string]: {
      totalHours: number;
      billableHours: number;
      employeeIds: Set<number>;
    };
  } = {};

  const relevantTimesheets = timesheets.filter(
    (ts) =>
      ts.weekEnding >= dateRange.start &&
      ts.weekEnding <= dateRange.end &&
      (ts.status === 'approved' || ts.status === 'locked')
  );

  relevantTimesheets.forEach((ts) => {
    const weekKey = format(ts.weekEnding, 'yyyy-MM-dd');

    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = {
        totalHours: 0,
        billableHours: 0,
        employeeIds: new Set(),
      };
    }

    ts.entries.forEach((entry) => {
      const project = projects.find((p) => p.id === entry.projectId);
      const totalEntryHours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);

      weeklyData[weekKey].totalHours += totalEntryHours;
      if (project?.billable) {
        weeklyData[weekKey].billableHours += totalEntryHours;
      }
      weeklyData[weekKey].employeeIds.add(ts.employeeId);
    });
  });

  return Object.entries(weeklyData)
    .map(([weekKey, data]) => {
      const utilisationRate = data.totalHours > 0 ? data.billableHours / data.totalHours : 0;

      return {
        weekEnding: new Date(weekKey),
        totalHours: data.totalHours,
        billableHours: data.billableHours,
        utilisationRate,
        employeeCount: data.employeeIds.size,
      };
    })
    .sort((a, b) => a.weekEnding.getTime() - b.weekEnding.getTime());
};

/**
 * Calculate overall utilisation summary
 */
export const calculateUtilisationSummary = (
  employeeMetrics: UtilisationMetrics[],
  dailyUtilisation: DailyUtilisation[],
  settings?: UtilisationSettings
): UtilisationSummary => {
  const totalEmployees = employeeMetrics.length;
  const totalHours = employeeMetrics.reduce((sum, m) => sum + m.totalHours, 0);
  const totalBillableHours = employeeMetrics.reduce((sum, m) => sum + m.billableHours, 0);
  const totalNonBillableHours = totalHours - totalBillableHours;
  const billablePercentage = totalHours > 0 ? (totalBillableHours / totalHours) * 100 : 0;

  const averageUtilisation =
    employeeMetrics.length > 0
      ? employeeMetrics.reduce((sum, m) => sum + m.utilisationRate, 0) / employeeMetrics.length
      : 0;

  // Use settings target rate if provided, otherwise fall back to constant
  const targetRate = settings?.targetUtilisationRate ?? TARGET_UTILISATION_RATE;
  const varianceFromTarget = averageUtilisation - targetRate;

  // Find peak and lowest utilisation days
  let peakDay = dailyUtilisation[0];
  let lowestDay = dailyUtilisation[0];

  dailyUtilisation.forEach((day) => {
    if (day.utilisationRate > peakDay.utilisationRate) {
      peakDay = day;
    }
    if (day.utilisationRate < lowestDay.utilisationRate) {
      lowestDay = day;
    }
  });

  return {
    totalEmployees,
    averageUtilisation,
    totalHours,
    totalBillableHours,
    totalNonBillableHours,
    billablePercentage,
    targetUtilisation: targetRate,
    varianceFromTarget,
    peakUtilisationDay: peakDay?.day || 'N/A',
    lowestUtilisationDay: lowestDay?.day || 'N/A',
  };
};

/**
 * Generate complete utilisation report data
 */
export const generateUtilisationReport = (
  timesheets: Timesheet[],
  employees: Employee[],
  projects: Project[],
  filters: UtilisationFilters,
  settings?: UtilisationSettings
): UtilisationReportData => {
  // Apply filters
  let filteredTimesheets = timesheets.filter(
    (ts) =>
      ts.weekEnding >= filters.dateRange.start &&
      ts.weekEnding <= filters.dateRange.end &&
      (ts.status === 'approved' || ts.status === 'locked')
  );

  let filteredEmployees = employees;
  if (filters.employees.length > 0) {
    filteredEmployees = employees.filter((e) => filters.employees.includes(e.id));
    filteredTimesheets = filteredTimesheets.filter((ts) =>
      filters.employees.includes(ts.employeeId)
    );
  }

  if (filters.departments.length > 0) {
    filteredEmployees = filteredEmployees.filter((e) =>
      filters.departments.includes(e.department)
    );
    const employeeIds = filteredEmployees.map((e) => e.id);
    filteredTimesheets = filteredTimesheets.filter((ts) => employeeIds.includes(ts.employeeId));
  }

  if (filters.projects.length > 0) {
    filteredTimesheets = filteredTimesheets.map((ts) => ({
      ...ts,
      entries: ts.entries.filter((e) => filters.projects.includes(e.projectId)),
    }));
  }

  // Calculate metrics
  const employeeMetrics = filteredEmployees.map((employee) =>
    calculateEmployeeUtilisation(employee, filteredTimesheets, projects, filters.dateRange)
  );

  const dailyUtilisation = calculateDailyUtilisation(
    filteredTimesheets,
    projects,
    filters.dateRange
  );

  const projectUtilisation = calculateProjectUtilisation(
    filteredTimesheets,
    projects,
    filteredEmployees,
    filters.dateRange
  );

  const trends = calculateUtilisationTrends(filteredTimesheets, projects, filters.dateRange);

  const summary = calculateUtilisationSummary(employeeMetrics, dailyUtilisation, settings);

  return {
    summary,
    employeeMetrics,
    dailyUtilisation,
    projectUtilisation,
    trends,
  };
};

/**
 * Export utilisation data to CSV format
 */
export const exportUtilisationToCSV = (data: UtilisationReportData): string => {
  const lines: string[] = [];

  // Summary section
  lines.push('UTILISATION SUMMARY');
  lines.push('Metric,Value');
  lines.push(`Total Employees,${data.summary.totalEmployees}`);
  lines.push(`Average Utilisation,${(data.summary.averageUtilisation * 100).toFixed(2)}%`);
  lines.push(`Total Hours,${data.summary.totalHours.toFixed(2)}`);
  lines.push(`Total Billable Hours,${data.summary.totalBillableHours.toFixed(2)}`);
  lines.push(`Total Non-Billable Hours,${data.summary.totalNonBillableHours.toFixed(2)}`);
  lines.push(`Billable Percentage,${data.summary.billablePercentage.toFixed(2)}%`);
  lines.push(`Target Utilisation,${(data.summary.targetUtilisation * 100).toFixed(2)}%`);
  lines.push(
    `Variance from Target,${(data.summary.varianceFromTarget * 100).toFixed(2)}%`
  );
  lines.push('');

  // Employee metrics
  lines.push('EMPLOYEE UTILISATION');
  lines.push(
    'Employee,Department,Total Hours,Billable Hours,Non-Billable Hours,Utilisation Rate,Target Hours,Variance,Peak Day,Peak Hours,Project Count'
  );
  data.employeeMetrics.forEach((m) => {
    lines.push(
      `${m.employeeName},${m.department},${m.totalHours.toFixed(2)},${m.billableHours.toFixed(2)},${m.nonBillableHours.toFixed(2)},${(m.utilisationRate * 100).toFixed(2)}%,${m.targetHours.toFixed(2)},${m.variance.toFixed(2)},${m.peakDay},${m.peakHours.toFixed(2)},${m.projectCount}`
    );
  });
  lines.push('');

  // Project utilisation
  lines.push('PROJECT UTILISATION');
  lines.push(
    'Project,Client,Total Hours,Billable Hours,Employee Count,Utilisation Rate,Is Billable'
  );
  data.projectUtilisation.forEach((p) => {
    lines.push(
      `${p.projectName},${p.clientName},${p.totalHours.toFixed(2)},${p.billableHours.toFixed(2)},${p.employeeCount},${(p.utilisationRate * 100).toFixed(2)}%,${p.isBillable ? 'Yes' : 'No'}`
    );
  });
  lines.push('');

  // Daily utilisation
  lines.push('DAILY UTILISATION');
  lines.push('Day,Total Hours,Billable Hours,Non-Billable Hours,Utilisation Rate');
  data.dailyUtilisation.forEach((d) => {
    lines.push(
      `${d.day},${d.totalHours.toFixed(2)},${d.billableHours.toFixed(2)},${d.nonBillableHours.toFixed(2)},${(d.utilisationRate * 100).toFixed(2)}%`
    );
  });

  return lines.join('\n');
};

// ============================================================================
// PHASE 3: COMPARISON MODE FUNCTIONS
// ============================================================================

/**
 * Calculate comparison date range based on comparison mode
 */
export const calculateComparisonDateRange = (
  currentDateRange: { start: Date; end: Date },
  comparisonMode: ComparisonMode
): { start: Date; end: Date } | null => {
  if (!comparisonMode.enabled) return null;

  const daysDiff = differenceInDays(currentDateRange.end, currentDateRange.start);

  switch (comparisonMode.type) {
    case 'previous-period':
      // Go back by the same number of days
      const prevEnd = subDays(currentDateRange.start, 1);
      const prevStart = subDays(prevEnd, daysDiff);
      return { start: prevStart, end: prevEnd };

    case 'year-over-year':
      // Go back exactly one year
      return {
        start: subYears(currentDateRange.start, 1),
        end: subYears(currentDateRange.end, 1),
      };

    case 'custom':
      // Use the custom date range if provided
      if (comparisonMode.comparisonDateRange) {
        return comparisonMode.comparisonDateRange;
      }
      return null;

    default:
      return null;
  }
};

/**
 * Calculate variance between two values
 */
const calculateVariance = (
  current: number,
  comparison: number
): { absolute: number; percentage: number; trend: 'up' | 'down' | 'stable' } => {
  const absolute = current - comparison;
  const percentage = comparison !== 0 ? (absolute / comparison) * 100 : 0;
  
  // Determine trend (consider < 1% as stable)
  let trend: 'up' | 'down' | 'stable';
  if (Math.abs(percentage) < 1) {
    trend = 'stable';
  } else if (absolute > 0) {
    trend = 'up';
  } else {
    trend = 'down';
  }

  return { absolute, percentage, trend };
};

/**
 * Compare two employee utilisation metrics
 */
export const compareEmployeeMetrics = (
  current: UtilisationMetrics,
  comparison: UtilisationMetrics
): ComparisonMetrics => {
  return {
    current,
    comparison,
    variance: calculateVariance(current.utilisationRate, comparison.utilisationRate),
  };
};

/**
 * Compare two utilisation summaries
 */
export const compareUtilisationSummaries = (
  current: UtilisationSummary,
  comparison: UtilisationSummary
): ComparisonSummary => {
  return {
    current,
    comparison,
    variance: {
      totalHours: calculateVariance(current.totalHours, comparison.totalHours),
      billableHours: calculateVariance(current.totalBillableHours, comparison.totalBillableHours),
      utilisationRate: calculateVariance(current.averageUtilisation, comparison.averageUtilisation),
      activeEmployees: calculateVariance(current.totalEmployees, comparison.totalEmployees),
    },
  };
};

// ============================================================================
// PHASE 3: CAPACITY PLANNING FUNCTIONS
// ============================================================================

const OPTIMAL_UTILISATION_MIN = 70;
const OPTIMAL_UTILISATION_MAX = 100;

/**
 * Get employee's working days per week from their working pattern
 */
const getEmployeeWorkingDaysPerWeek = (employee: Employee): number => {
  if (employee.workingPattern?.workingDays) {
    const workingDays = Object.values(employee.workingPattern.workingDays).filter(
      (day) => day.isWorkingDay
    );
    return workingDays.length;
  }
  // Fallback to 5 days if no working pattern is set
  return 5;
};

/**
 * Get employee's expected hours per day from their working pattern
 */
const getEmployeeExpectedHoursPerDay = (employee: Employee): number => {
  const expectedHoursPerWeek = getEmployeeExpectedHoursPerWeek(employee);
  const workingDaysPerWeek = getEmployeeWorkingDaysPerWeek(employee);
  return expectedHoursPerWeek / workingDaysPerWeek;
};

/**
 * Calculate capacity metrics for an employee
 */
export const calculateCapacityMetrics = (
  employee: Employee,
  timesheets: Timesheet[],
  projects: Project[],
  dateRange: { start: Date; end: Date }
): CapacityMetrics => {
  // Calculate total capacity based on employee's working pattern
  const totalDays = differenceInDays(dateRange.end, dateRange.start) + 1;
  const workingDaysPerWeek = getEmployeeWorkingDaysPerWeek(employee);
  const workingDays = Math.floor(totalDays * (workingDaysPerWeek / 7));
  const hoursPerDay = getEmployeeExpectedHoursPerDay(employee);
  const totalCapacity = workingDays * hoursPerDay;

  // Calculate allocated hours
  const employeeTimesheets = timesheets.filter(
    (ts) =>
      ts.employeeId === employee.id &&
      ts.weekEnding >= dateRange.start &&
      ts.weekEnding <= dateRange.end &&
      (ts.status === 'approved' || ts.status === 'locked')
  );

  let allocatedHours = 0;
  employeeTimesheets.forEach((ts) => {
    ts.entries.forEach((entry) => {
      const entryHours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);
      allocatedHours += entryHours;
    });
  });

  // Calculate metrics
  const availableCapacity = totalCapacity - allocatedHours;
  const utilisationRate = totalCapacity > 0 ? (allocatedHours / totalCapacity) * 100 : 0;

  // Determine status
  let status: CapacityMetrics['status'];
  if (utilisationRate < OPTIMAL_UTILISATION_MIN) {
    status = 'under-utilised';
  } else if (utilisationRate > OPTIMAL_UTILISATION_MAX) {
    status = 'over-allocated';
  } else {
    status = 'optimal';
  }

  // Simple forecast (based on current utilisation rate)
  const weeklyRate = allocatedHours / (totalDays / 7);
  const forecast = {
    nextWeek: weeklyRate,
    nextMonth: weeklyRate * 4,
    nextQuarter: weeklyRate * 13,
  };

  return {
    employeeId: employee.id,
    employeeName: employee.name,
    totalCapacity,
    allocatedHours,
    availableCapacity,
    utilisationRate,
    status,
    forecast,
  };
};

/**
 * Calculate capacity summary for all employees
 */
export const calculateCapacitySummary = (
  capacityMetrics: CapacityMetrics[]
): CapacitySummary => {
  const totalCapacity = capacityMetrics.reduce((sum, m) => sum + m.totalCapacity, 0);
  const totalAllocated = capacityMetrics.reduce((sum, m) => sum + m.allocatedHours, 0);
  const totalAvailable = totalCapacity - totalAllocated;

  const overAllocatedCount = capacityMetrics.filter((m) => m.status === 'over-allocated').length;
  const underUtilisedCount = capacityMetrics.filter((m) => m.status === 'under-utilised').length;
  const optimalCount = capacityMetrics.filter((m) => m.status === 'optimal').length;

  const averageUtilisation =
    capacityMetrics.length > 0
      ? capacityMetrics.reduce((sum, m) => sum + m.utilisationRate, 0) / capacityMetrics.length
      : 0;

  // Generate recommendations
  const recommendations: CapacityRecommendation[] = [];

  // Check for over-allocation
  if (overAllocatedCount > 0) {
    const overAllocatedEmployees = capacityMetrics
      .filter((m) => m.status === 'over-allocated')
      .map((m) => m.employeeName);

    recommendations.push({
      type: 'reduce-workload',
      priority: 'high',
      message: `${overAllocatedCount} employee(s) are over-allocated and may be at risk of burnout.`,
      affectedEmployees: overAllocatedEmployees,
      estimatedImpact: 'Reduce workload or redistribute tasks to prevent burnout',
      actionItems: [
        'Review current project assignments',
        'Consider redistributing tasks to under-utilised team members',
        'Evaluate project deadlines and priorities',
      ],
    });
  }

  // Check for under-utilisation
  if (underUtilisedCount > 0) {
    const underUtilisedEmployees = capacityMetrics
      .filter((m) => m.status === 'under-utilised')
      .map((m) => m.employeeName);

    recommendations.push({
      type: 'reallocate',
      priority: 'medium',
      message: `${underUtilisedCount} employee(s) have available capacity that could be utilized.`,
      affectedEmployees: underUtilisedEmployees,
      estimatedImpact: 'Increase billable hours and team productivity',
      actionItems: [
        'Assign additional projects or tasks',
        'Consider training or professional development',
        'Review project pipeline for new opportunities',
      ],
    });
  }

  // Check if team is generally well-balanced
  if (optimalCount === capacityMetrics.length) {
    recommendations.push({
      type: 'maintain',
      priority: 'low',
      message: 'All team members are optimally utilised. Great work!',
      affectedEmployees: [],
      estimatedImpact: 'Continue current allocation strategy',
      actionItems: [
        'Monitor utilisation regularly',
        'Be prepared for upcoming project changes',
        'Maintain current team balance',
      ],
    });
  }

  return {
    totalCapacity,
    totalAllocated,
    totalAvailable,
    overAllocatedCount,
    underUtilisedCount,
    optimalCount,
    averageUtilisation,
    recommendations,
  };
};
